/* eslint-disable @typescript-eslint/no-explicit-any */
import { RichText } from "@sitecore-jss/sitecore-jss-nextjs";
import axios from "axios";
import Image from "next/image";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider } from "styled-components";
import IsRtl from "./isRtl";
import { DGAButton } from "./dga-button/DGAButton";

// ChatBot will be loaded dynamically on client-side only
let ChatBot: any = null;

type ContentProp = {
  promptText: string;
  welcomeText: string;
  optionsSelectionText: string;
  fallbackText: string;
  thankYouText: string;
  onlineText: string;
};
type Language = "ar" | "en" | "zh" | "ko";

const ChatHeader = ({ toggleChatbot, header, online }: any) => {
  const { locale } = useRouter();

  function getOrdinal(day: any) {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  const now = new Date();
  const day = now.getDate();
  const month = now.toLocaleString("en-US", { month: "long" });
  const hour = now.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

  const result = `${month} ${day}${getOrdinal(day)} at ${hour}`;
  return (
    <>
      <div
        className={`flex px-2 py-4 bg-[#1B8354] justify-between items-center h-lg:p-4 ${
          locale == "ar" ? "rtl" : ""
        }`}
      >
        <div className={`flex gap-4 items-center`}>
          <div>
            <div className="">
              <svg
                width="44"
                height="44"
                viewBox="0 0 44 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M22 1C33.598 1 43 10.402 43 22C43 33.598 33.598 43 22 43C10.402 43 1 33.598 1 22C1 10.402 10.402 1 22 1Z"
                  fill="#F3F4F6"
                  stroke="white"
                  strokeWidth="2"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.0181 20.0308C11.3474 14.2515 16.1382 9.6665 22 9.6665C27.8618 9.6665 32.6526 14.2515 32.9819 20.0308C31.851 18.9775 30.3341 18.3332 28.6667 18.3332C26.6416 18.3332 25 19.9748 25 21.9998V27.3332C25 29.3582 26.6416 30.9998 28.6667 30.9998C30.3355 30.9998 31.8536 30.3544 32.9849 29.2995C32.9766 29.631 32.9638 29.9277 32.944 30.1978C32.8893 30.9427 32.7852 31.4074 32.6182 31.7783C32.1837 32.7438 31.4107 33.5169 30.4451 33.9514C30.0742 34.1183 29.6095 34.2224 28.8646 34.2771C28.1098 34.3325 27.1485 34.3332 25.7949 34.3332H22C21.4477 34.3332 21 34.7809 21 35.3332C21 35.8855 21.4477 36.3332 22 36.3332H25.8343C27.1398 36.3332 28.1745 36.3332 29.0111 36.2718C29.8661 36.209 30.5929 36.0781 31.2659 35.7752C32.6771 35.1401 33.8069 34.0103 34.442 32.5991C34.745 31.926 34.8758 31.1993 34.9386 30.3443C35 29.5076 35 28.473 35 27.1675V20.6665C35 13.4868 29.1797 7.6665 22 7.6665C14.8203 7.6665 9 13.4868 9 20.6665V24.6665C9 28.1643 11.8355 30.9998 15.3333 30.9998C17.3584 30.9998 19 29.3582 19 27.3332V21.9998C19 19.9748 17.3584 18.3332 15.3333 18.3332C13.6659 18.3332 12.149 18.9775 11.0181 20.0308ZM11 24.6665C11 27.0597 12.9401 28.9998 15.3333 28.9998C16.2538 28.9998 17 28.2536 17 27.3332V21.9998C17 21.0794 16.2538 20.3332 15.3333 20.3332C12.9401 20.3332 11 22.2733 11 24.6665ZM33 24.6665C33 22.2733 31.0599 20.3332 28.6667 20.3332C27.7462 20.3332 27 21.0794 27 21.9998V27.3332C27 28.2536 27.7462 28.9998 28.6667 28.9998C31.0599 28.9998 33 27.0597 33 24.6665Z"
                  fill="#0F2837"
                />
              </svg>
            </div>
          </div>
          <div className="flex flex-col text-white">
            <div className="text-sm font-bold mb-2">{header}</div>
            {online && (
              <div>
                <div className="flex items-center justify-center gap-1 rounded-xl p-1 bg-green-200 w-fit">
                  <div className="w-[10px] h-[10px] rounded-full bg-green-800"></div>
                  <div className="text-green-800 text-[10px]  leading-3 ">{online}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DGAButton
          onClick={toggleChatbot}
          size="iconOnly md"
          variant="primary"
          className="!outline-none !ring-0 !ring-offset-0"
          aria-label="Close chatbot"
          buttonContent={
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.558059 0.558058C0.802136 0.313981 1.19786 0.313981 1.44194 0.558058L6 5.11612L10.5581 0.558059C10.8021 0.313981 11.1979 0.313981 11.4419 0.558059C11.686 0.802136 11.686 1.19786 11.4419 1.44194L6.88388 6L11.4419 10.5581C11.686 10.8021 11.686 11.1979 11.4419 11.4419C11.1979 11.686 10.8021 11.686 10.5581 11.4419L6 6.88388L1.44194 11.4419C1.19786 11.686 0.802136 11.686 0.558058 11.4419C0.313981 11.1979 0.313981 10.8021 0.558058 10.5581L5.11612 6L0.558059 1.44194C0.313981 1.19786 0.313981 0.802136 0.558059 0.558058Z"
                fill="white"
              />
            </svg>
          }
        />
      </div>
      <div className="my-2 flex justify-center text-[#384250] text-[10px]">{result}</div>
    </>
  );
};
const ChatBotComponent = (): JSX.Element => {
  const [chatAnalyticsPostBody, setChatAnalyticsPostBody] = useState<{
    SerivceTitle: string[];
    ServiceQuestion: string[];
    SessionId: string;
    // Location: string; // Commented out location tracking
    LastStepName: string;
    Rating: number | null;
    Language: string;
  }>({
    Language: "",
    SerivceTitle: [],
    ServiceQuestion: [],
    SessionId: "",
    // Location: "", // Commented out location tracking
    LastStepName: "",
    Rating: null,
  });
  const chatAnalyticsRef = useRef(chatAnalyticsPostBody);
  chatAnalyticsRef.current = chatAnalyticsPostBody;

  // useEffect(() => {

  // }, [chatAnalyticsPostBody]);

  const [loading, setLoading] = useState(true);
  const [chatData, setChatData] = useState<ContentProp | any>(null);
  const { locale } = useRouter();
  const language = ((): Language => {
    const raw = ((locale || "en") as string).toString().slice(0, 2).toLowerCase();
    if (raw === "ar" || raw === "en" || raw === "zh" || raw === "ko") return raw;
    return "en";
  })();
  const [isOpen, setIsOpen] = useState(false);
  const [chatBotLoaded, setChatBotLoaded] = useState(false);
  const isRtl = IsRtl();

  // Load ChatBot only on client-side using dynamic import
  useEffect(() => {
    if (typeof window !== 'undefined' && !ChatBot) {
      // @ts-ignore - react-simple-chatbot doesn't have type definitions
      import("react-simple-chatbot").then((module: any) => {
        ChatBot = module.default;
        setChatBotLoaded(true);
      }).catch(() => {
        // Handle error silently
        console.error("Failed to load ChatBot");
      });
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_SITECORE_REST}/api/ChatBot/GetChatBot`,
        { culture: locale },
        {
          headers: {
            "Content-Type": "application/json",
          },
          //   withCredentials: true, // if needed to send cookies
        }
      );
      setChatData(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      console.error("Failed to fetch chatbot data:", error);
      return null;
    }
  };
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);
  const StarRating = ({ triggerNextStep }: any) => {
    const [selected, setSelected] = useState<number>(0);
    const stars = ["😡", "😕", "😐", "😊", "🤩"];

    const handleRating = (index: number) => {
      setSelected(index + 1);
      setTimeout(() => {
        triggerNextStep({ value: index + 1 });
      }, 500);
    };
    const renderRating = (ratingIcon: any, index: number): any => {
      return (
        <div
          style={{
            cursor: "pointer",
            marginRight: "10px",
            opacity: selected === index + 1 ? 1 : 0.6,
          }}
          onClick={() => handleRating(index)}
        >
          {!chatData?.showIcon && <span>{stars[index]}</span>}
          {ratingIcon && chatData?.showIcon && (
            <div className="inline-block w-[24px] h-[24px] relative">
              <Image
                src={process.env.NEXT_PUBLIC_SITECORE_API_HOST + ratingIcon}
                alt={`rating-icon-${index}`}
                layout="fill"
              />
            </div>
          )}
        </div>
      );
    };
    return (
      <div>
        <p>{chatData?.ratingTitle}</p>
        <div className="flex" style={{ fontSize: "24px" }}>
          {/* <RenderRating ratingIcon={chatData?.firstRatingIcon} index={0} /> */}
          {renderRating(chatData?.firstRatingIcon, 0)}
          {renderRating(chatData?.secondRatingIcon, 1)}
          {renderRating(chatData?.thirdRatingIcon, 2)}
          {renderRating(chatData?.fourthRatingIcon, 3)}
          {renderRating(chatData?.fifthRatingIcon, 4)}
          {/* renderRating(ratingIcon={chatData?.firstRatingIcon}, index={0}) */}
          {/* {stars.map((star, index) => (
          ))} */}
        </div>
      </div>
    );
  };
  const content = {
    en: {
      promptText: chatData?.promptText,
      welcome: chatData?.welcomeText,
      selectOption: chatData?.optionsSelectionText,
      otherResponse:
        chatData?.serviceCategories[chatData?.serviceCategories.length - 1]?.questions[0]
          ?.description,
      returnPrompt: chatData?.restartText,
      fallback: chatData?.fallbackText,
      thankYou: chatData?.thankYouText,
      responses: {
        "General Questions": "You can find general information here...",
      } as any,
    },
    ar: {
      promptText: chatData?.promptText,
      welcome: chatData?.welcomeText,
      selectOption: chatData?.optionsSelectionText,
      otherResponse:
        chatData?.serviceCategories[chatData?.serviceCategories.length - 1]?.questions[0]
          ?.description,
      returnPrompt: chatData?.restartText,
      fallback: chatData?.fallbackText,
      thankYou: chatData?.thankYouText,
      responses: {
        "الأسئلة العامة": "يمكنك العثور على المعلومات العامة هنا...",
      },
    },
    zh: {
      promptText: chatData?.promptText,
      welcome: chatData?.welcomeText,
      selectOption: chatData?.optionsSelectionText,
      otherResponse:
        chatData?.serviceCategories[chatData?.serviceCategories.length - 1]?.questions[0]
          ?.description,
      returnPrompt: chatData?.restartText,
      fallback: chatData?.fallbackText,
      thankYou: chatData?.thankYouText,
      responses: {
        常见问题: "您可以在这里找到常规信息...", // Chinese
      },
    },
    ko: {
      promptText: chatData?.promptText,
      welcome: chatData?.welcomeText,
      selectOption: chatData?.optionsSelectionText,
      otherResponse:
        chatData?.serviceCategories[chatData?.serviceCategories.length - 1]?.questions[0]
          ?.description,
      returnPrompt: chatData?.restartText,
      fallback: chatData?.fallbackText,
      thankYou: chatData?.thankYouText,
      responses: {
        "자주 묻는 질문": "여기에서 일반 정보를 찾을 수 있습니다...", // Korean
      },
    },
  };
  //   htis is
  // const lang = language || 'ar';
  const t = content[language as Language];

  const services = useMemo((): string[] => {
    const raw =
      (chatData?.serviceCategories?.map((item: any) => item?.title).filter(Boolean) as string[]) ??
      [];
    return [...new Map(raw.map((title: string) => [title, title])).values()];
  }, [chatData?.serviceCategories]);

  const getQuestionsForService = useCallback(
    (title: string) => {
      return (
        chatData?.serviceCategories?.find((cat: any) => cat?.title === title)?.questions || []
      );
    },
    [chatData?.serviceCategories]
  );

  const createServiceStep = useCallback(
    (label: any) => {
      const hasQuestions = getQuestionsForService(label).length > 0;

      return hasQuestions
        ? {
            id: `answer-${label}`,
            message:
              (locale == "ar"
                ? "الإختيار: "
                : locale == "zh"
                ? "您选择了："
                : locale == "ko"
                ? "선택한 항목: "
                : "You selected: ") + `${label}`,
            trigger: () => {
              setChatAnalyticsPostBody((prev) => ({
                ...prev,
                SerivceTitle: [...prev.SerivceTitle, `${label}`],
                LastStepName: `${label}`,
              }));
              return `questions-${label}`;
            },
          }
        : {
            id: `answer-${label}`,
            message: t.responses?.[label] || t.otherResponse,
            trigger: "returnOrEnd",
          };
    },
    // Do not depend on `t.responses` (new object every render); `chatData` covers CMS updates.
    [getQuestionsForService, locale, setChatAnalyticsPostBody, t.otherResponse, chatData]
  );

  const createQuestionSteps = useCallback(
    (serviceTitle: string) => {
      const raw = getQuestionsForService(serviceTitle);
      const seen = new Set<string>();
      const questions = raw.filter((q: any) => {
        const key = `${(q?.title || "").trim()}|${(q?.description || "").slice(0, 80)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (!questions.length) return [];
      return [
        {
          id: `questions-${serviceTitle}`,
          message: t.selectOption, // display entered service
          trigger: () => {
            return `question-options-${serviceTitle}`;
          },
        },
        {
          id: `question-options-${serviceTitle}`,
          options: questions.map((q: any, i: number) => ({
            value: q.title || `q${i}`,
            label: q.title || `Question ${i + 1}`,
            trigger: () => {
              return `question-answer-${serviceTitle}-${i}`;
            },
          })),
        },
        ...questions.map((q: any, i: number) => ({
          id: `question-answer-${serviceTitle}-${i}`,
          component: (
            <>
              <RichText field={{ value: q.description }} />
            </>
          ),
          asMessage: true,
          trigger: () => {
            setChatAnalyticsPostBody((prev) => ({
              ...prev,
              LastStepName: `${q.title}`,
              ServiceQuestion: [
                ...prev.ServiceQuestion,
                `question-${q.title}--answer-${q.description}`,
              ],
            }));
            return "returnOrEnd";
          },
        })),
      ];
    },
    [getQuestionsForService, setChatAnalyticsPostBody, t.selectOption]
  );

  const serviceOptions = useMemo(
    () =>
      services.map((label) => ({
        value: label,
        label,
        trigger: () => {
          setChatAnalyticsPostBody((prev) => ({
            ...prev,
            LastStepName: `${label}`,
          }));
          return label === "Other" || label === "أخرى" ? "otherAnswer" : `answer-${label}`;
        },
      })),
    [services, setChatAnalyticsPostBody]
  );
  const RatingComp = ({ previousStep }: any) => {
    const emojis = ["😡", "😕", "😐", "😊", "🤩"];
    const ratingText = {
      1: chatData?.fifthRating,
      2: chatData?.fourthRating,
      3: chatData?.thirdRating,
      4: chatData?.secondRating,
      5: chatData?.firstRating,
    } as any;
    return (
      <>
        <div>{ratingText[previousStep?.value]}</div>
        {!chatData?.showIcon && <div>{emojis[previousStep?.value - 1]}</div>}
      </>
    );
  };
  const steps = useMemo(
    () => [
      {
        id: "intro",
        message: t.promptText,
        trigger: "welcome",
      },
      {
        id: "welcome",
        message: () => t.welcome,
        trigger: () => {
          setChatAnalyticsPostBody((prev) => ({
            ...prev,
            LastStepName: "Initial",
          }));
          return "select-service";
        },
      },
      {
        id: "select-service",
        message: () => t.selectOption,
        trigger: () => {
          return "service-options";
        },
      },
      {
        id: "service-options",
        options: serviceOptions,
      },
      ...services
        .filter((label) => label !== "Other" && label !== "أخرى")
        .flatMap((label) => [createServiceStep(label), ...createQuestionSteps(label)]),
      {
        id: "otherAnswer",
        component: (
          <>
            <RichText field={{ value: t.otherResponse }} />
          </>
        ),
        asMessage: true,
        trigger: "returnOrEnd",
      },
      {
        id: "returnOrEnd",
        options: [
          {
            value: 1,
            label: chatData?.restartText,
            trigger: () => {
              setChatAnalyticsPostBody((prev) => ({
                ...prev,
                LastStepName: `${chatData?.restartText}`,
              }));
              return "select-service";
            },
          },
          {
            value: 2,
            label: chatData?.endText,
            trigger: () => {
              setChatAnalyticsPostBody((prev) => ({
                ...prev,
                LastStepName: `${chatData?.endText}`,
              }));
              return "feedback";
            },
          },
        ],
      },
      {
        id: "return-check",
        user: true,
        trigger: (value: any) => {
          return value.value === "0" ? "select-service" : "feedback";
        },
      },
      {
        id: "fallback",
        message: () => t.fallback,
        trigger: "returnOrEnd",
      },
      {
        id: "feedback",
        component: <StarRating />,
        waitAction: true,
        asMessage: true,
        trigger: "rating-response",
      },
      {
        id: "rating-response",
        component: <RatingComp />,
        asMessage: true,
        trigger: "goodbye",
      },
      {
        id: "goodbye",
        message: () => t.thankYou,
        end: true,
      },
    ],
    [
      chatData?.endText,
      chatData?.restartText,
      createQuestionSteps,
      createServiceStep,
      serviceOptions,
      services,
      setChatAnalyticsPostBody,
      t.fallback,
      t.otherResponse,
      t.promptText,
      t.selectOption,
      t.thankYou,
      t.welcome,
    ]
  );

  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(()=>{
    if (isFirstRender && isOpen) {
          setIsFirstRender(false);
    }
  },[isOpen])
  // COMMENTED OUT: Location tracking code for privacy
  // get user country without permission serverside
  // useEffect(() => {
  //   if (isFirstRender && isOpen) {
  //     setIsFirstRender(false);
  //     const fetchLocation = async () => {
  //       try {
  //         // user request own api

  //         //try getting it with https://api.ipify.org?format=json
  //         const ipifyResponse = await fetch("https://api.ipify.org?format=json");
  //         // if (ipifyResponse.ok) {
  //         const ipifyData = await ipifyResponse.json();
  //         const userProvidedIP = ipifyData.ip;
  //         // }
  //         // }

  //         // const res = await fetch("/api/ip-address/ipLocation");
  //         const res = await axios.post("/api/ip-address/ipLocation", {
  //           userProvidedIP: userProvidedIP || null,
  //         });
  //         // const data = await res.json();

  //         if (res?.data?.success && res?.data.country) {
  //           setChatAnalyticsPostBody((prev) => ({
  //             ...prev,
  //             Location: res?.data.country,
  //           }));
  //         } else {
  //           console.warn("Could not get country info from API:", res?.data);

  //           // Ask for browser geolocation permission
  //           if ("geolocation" in navigator) {
  //             navigator.geolocation.getCurrentPosition(
  //               async (position) => {
  //                 const { latitude, longitude } = position.coords;

  //                 //api.bigdatacloud.net/data/reverse-geocode-client?latitude=45.4215&longitude=-75.6972&localityLanguage=en
  //                 // get country from lat/lng
  //                 const res = await fetch(
  //                   `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
  //                 );
  //                 const data = await res.json();
  //                 const country = data?.countryName;

  //                 // You can reverse geocode this lat/lng to country using some API or logic
  //                 // Or just store lat/lng for analytics
  //                 setChatAnalyticsPostBody((prev) => ({
  //                   ...prev,
  //                   Location: country,
  //                 }));
  //               },
  //               (error) => {
  //                 console.error("User denied geolocation or error:", error);
  //                 setChatAnalyticsPostBody((prev) => ({
  //                   ...prev,
  //                   Location: error?.code === 1 ? error?.message : "LAT/LNG to Country API Failed",
  //                 }));
  //               }
  //             );
  //           } else {
  //             console.warn("Geolocation not supported in this browser");
  //           }
  //         }
  //       } catch (error) {
  //         console.error("Failed to fetch location info:", error);
  //       }
  //     };

  //     fetchLocation();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isOpen]);

  useEffect(() => {
    if (!isFirstRender || !isOpen) return;
    const storageKey = "moc_chatbot_analytics_session_id";
    try {
      let sessionId = sessionStorage.getItem(storageKey);
      if (!sessionId) {
        sessionId =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem(storageKey, sessionId);
      }
      setChatAnalyticsPostBody(
        (prev) =>
          ({
            ...prev,
            SessionId: sessionId as string,
            Language: locale,
          } as any)
      );
    } catch {
      const sessionId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      setChatAnalyticsPostBody(
        (prev) =>
          ({
            ...prev,
            SessionId: sessionId,
            Language: locale,
          } as any)
      );
    }
  }, [isOpen, isFirstRender, locale]);

  const toggleChatbot = useCallback((): void => {
    setIsOpen((prev: boolean) => {
      if (prev) {
        void axios
          .post(
            `${process.env.NEXT_PUBLIC_SITECORE_API_HOST}/api/Chatbot/submissions`,
            chatAnalyticsRef.current,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .catch((_error: unknown) => {
            // Drop-off analytics — ignore failures
          });
      }
      return !prev;
    });
  }, []);

  const handleEnd = useCallback(({ values }: any) => {
    const filteredValues = values.filter((item: number | string, index: number) => {
      if (typeof item === "string" || index === values.length - 1) {
        return item;
      } else {
        return null;
      }
    });
    const filteredServices = filteredValues.filter((item: number | string, index: number) => {
      if (typeof item === "string" && index % 2 == 0) {
        return item;
      } else {
        return null;
      }
    });
    const filteredQuestions = filteredValues.filter((item: number | string, index: number) => {
      if (typeof item === "string" && index % 2 != 0) {
        return item;
      } else {
        return null;
      }
    });

    axios
      .post(
        `${process.env.NEXT_PUBLIC_SITECORE_API_HOST}/api/Chatbot/submissions`,
        {
          SerivceTitle: filteredServices,
          ServiceQuestion: filteredQuestions,
          Rating: filteredValues[filteredValues.length - 1],
          Language: locale,
          SessionId: chatAnalyticsRef.current.SessionId,
          LastStepName: filteredValues[filteredValues.length - 2] || "",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .catch((error) => {
        console.error("Submission error:", error);
      });
    setTimeout(() => {
      setIsOpen(true);
    }, 4000);
  }, [locale]);

  const theme = useMemo(
    () => ({
      background: "#fff",
      headerBgColor: "#1B8354",
      headerFontColor: "#fffff",
      headerFontSize: "15px",
      botBubbleColor: "#E5E7EB",
      botFontColor: "#fff",
      userBubbleColor: "#eee",
      userFontColor: "#000",
    }),
    []
  );

  const chatHeader = useMemo(
    () => (
      <ChatHeader
        toggleChatbot={toggleChatbot}
        header={chatData?.chatBotTitle}
        online={chatData?.onlineText}
      />
    ),
    [toggleChatbot, chatData?.chatBotTitle, chatData?.onlineText]
  );

  const chatNavLabel =
    locale === "ar"
      ? "الدردشة والمساعدة"
      : locale === "zh"
      ? "聊天和帮助"
      : locale === "ko"
      ? "채팅 및 도움말"
      : "Chat and help";

  return (!loading && (
    <ThemeProvider theme={theme}>
      <nav
        aria-label={chatNavLabel}
        className={` fixed bottom-5 ${
          !isRtl ? "right-5" : "right-5"
        } z-[500] flex flex-col items-end gap-2`}
      >
        {isOpen && !loading && chatBotLoaded && ChatBot && (
          <ChatBot
            steps={steps}
            headerComponent={chatHeader}
            avatar={true}
            botAvatar={null}
            userAvatar={null}
            hideBotAvatar
            hideUserAvatar
            botDelay={500}
            userDelay={300}
            placeholder={
              locale === "ar"
                ? "إرسال"
                : locale === "ko"
                ? "메시지"
                : locale === "zh"
                ? "留言"
                : "Message"
            }
            recognitionEnable={false}
            handleEnd={handleEnd}
            floating={true}
          />
        )}
        {!loading && (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              aria-label={
                locale === "ar"
                  ? isOpen
                    ? "إغلاق نافذة الدردشة"
                    : "فتح نافذة الدردشة"
                  : locale === "zh"
                  ? isOpen
                    ? "关闭聊天"
                    : "打开聊天"
                  : locale === "ko"
                  ? isOpen
                    ? "채팅 닫기"
                    : "채팅 열기"
                  : isOpen
                  ? "Close chat"
                  : "Open chat"
              }
              className="cursor-pointer p-4 bg-[#1B8354] hover:bg-primary-700 active:bg-primary-800 z-50 rounded-full border-0"
              onClick={toggleChatbot}
            >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg" aria-hidden={true} focusable="false">
              <path
                d="M2.81802 19.637C3.21502 19.71 3.61599 19.746 4.01699 19.746L4.01802 19.7471C5.10002 19.7471 6.17601 19.4851 7.13501 18.9761L7.23398 18.925L7.26221 18.9106C7.42408 18.8283 7.58602 18.7458 7.67202 18.709C7.70802 18.713 7.76801 18.72 7.86201 18.738C8.27001 18.814 8.66099 18.5461 8.73799 18.1391C8.81399 17.7321 8.54601 17.34 8.13901 17.263C7.86001 17.211 7.66298 17.1961 7.47998 17.2141C7.45398 17.2171 7.42698 17.2211 7.40098 17.2261C7.16998 17.2741 6.96398 17.3791 6.55298 17.5891L6.44902 17.642L6.43799 17.648C5.76299 18.007 5.013 18.208 4.252 18.242C4.447 17.828 4.591 17.387 4.679 16.929C4.806 16.254 4.55998 15.545 4.00298 14.982C2.55098 13.522 1.752 11.615 1.752 9.61104C1.752 5.27704 5.48801 1.75107 10.081 1.75107C14.085 1.75107 17.527 4.44107 18.266 8.14707C18.347 8.55307 18.741 8.81503 19.148 8.73604C19.554 8.65504 19.818 8.26 19.737 7.854C18.859 3.448 14.798 0.25 10.08 0.25C4.65998 0.25 0.250977 4.44906 0.250977 9.61006C0.250977 12.0121 1.20499 14.296 2.93599 16.037C3.03299 16.135 3.252 16.39 3.204 16.648C3.077 17.307 2.79098 17.92 2.37598 18.421C2.20598 18.626 2.15802 18.9061 2.24902 19.1561C2.33902 19.4061 2.55602 19.589 2.81802 19.637Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.143 21.242C17.7679 21.5769 18.4657 21.7499 19.1675 21.75C19.4313 21.75 19.6952 21.725 19.956 21.6761C20.216 21.6271 20.43 21.4451 20.521 21.1971C20.611 20.9491 20.565 20.671 20.398 20.466C20.173 20.19 20.024 19.87 19.956 19.517C19.96 19.499 19.981 19.452 20.029 19.405L20.038 19.396C21.141 18.277 21.749 16.808 21.749 15.259C21.749 11.945 18.945 9.24902 15.499 9.24902C12.053 9.24902 9.24902 11.945 9.24902 15.259C9.24902 18.573 12.053 21.269 15.499 21.269C15.917 21.269 16.319 21.231 16.695 21.157C16.7105 21.1545 16.7256 21.1521 16.7403 21.1497C16.772 21.1446 16.8017 21.1398 16.829 21.1351C16.855 21.1311 16.885 21.1261 16.911 21.1221C16.955 21.1441 17.013 21.1741 17.066 21.2021L17.143 21.242ZM17.761 19.8721C17.52 19.7461 17.355 19.66 17.157 19.629C16.96 19.593 16.784 19.6221 16.582 19.6561C16.5507 19.6615 16.5164 19.667 16.4791 19.6729C16.4686 19.6746 16.458 19.6763 16.447 19.678C16.437 19.679 16.427 19.681 16.417 19.683C16.133 19.74 15.825 19.769 15.5 19.769C12.881 19.769 10.75 17.746 10.75 15.259C10.75 12.772 12.881 10.749 15.5 10.749C18.119 10.749 20.25 12.772 20.25 15.259C20.25 16.409 19.797 17.502 18.975 18.338C18.57 18.738 18.386 19.279 18.482 19.788C18.507 19.923 18.541 20.056 18.581 20.187C18.327 20.133 18.08 20.0421 17.849 19.9181C17.846 19.9161 17.84 19.913 17.84 19.913L17.761 19.8721Z"
                fill="white"
              />
            </svg>
            </button>
        )}
      </nav>
    </ThemeProvider>
  )) as any;
};

export default ChatBotComponent;
